import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, UseFormTrigger } from "react-hook-form";
import z from "zod";

import { ACCEPTED_FILE_TYPES_DEFAULT, ACCEPT_ATTRIBUTE_DEFAULT, MAX_FILE_SIZE } from "@/constants";
import { getFileSize } from "@/utils/getFileSize";
import { getFormattedFileTypeErrorMessage } from "@/utils/validationMessages";
import FileUploadField from "../shared/formFields/fileUploader";
import Button from "../shared/ui/Button";
import Modal from "../shared/ui/Modal";

type Props = {
	isModalOpen: boolean;
	handelModalClose: () => void;
	setDocumentFile: (file: File) => void;
	isSaveChanges?: boolean;
	fileSubmit?: () => void;
	documentFile?: File | null;
	uniqueId?: string;
	trigger?: UseFormTrigger<any>;
};

const DocumentUploadModal = ({
	isModalOpen,
	handelModalClose,
	setDocumentFile,
	isSaveChanges,
	fileSubmit,
	documentFile,
	uniqueId,
	trigger,
}: Props) => {
	const {
		register,
		formState: { errors },
		setValue,
		setError,
	} = useForm<{
		document: FileList | null;
	}>({
		mode: "onChange",
		defaultValues: {
			document: null,
		},
		resolver: zodResolver(
			z.object({
				document: z
					.any()
					.refine((file) => {
						if (!file) return true;
						return file.size <= MAX_FILE_SIZE;
					}, `Max size is ${getFileSize(MAX_FILE_SIZE)}.`)
					.refine((file) => {
						if (!file) return true;
						return ACCEPTED_FILE_TYPES_DEFAULT.includes(file?.type);
					}, getFormattedFileTypeErrorMessage(ACCEPT_ATTRIBUTE_DEFAULT)),
			}),
		),
	});

	return (
		<>
			<Modal title="" isOpen={isModalOpen} closeModal={handelModalClose}>
				<>
					<FileUploadField
						label=""
						{...register("document", { required: false })}
						registerName="document"
						errorMessage={typeof errors.document?.message === "string" ? errors.document?.message : undefined}
						setValue={(x: any, y: any) => {
							setValue(x, y);
							setDocumentFile(y ?? null);
							!isSaveChanges && handelModalClose();
						}}
						setError={setError}
						uniqueId={uniqueId}
						trigger={trigger}
						acceptedTypes={ACCEPTED_FILE_TYPES_DEFAULT}
						acceptAttribute={ACCEPT_ATTRIBUTE_DEFAULT}
					/>
					{isSaveChanges && (
						<Button
							onClick={() => {
								handelModalClose();
								fileSubmit && fileSubmit();
							}}
							className="mt-4"
							variant={documentFile ? "solid" : "ghost"}
							disabled={documentFile ? false : true}
						>
							Save Changes
						</Button>
					)}
				</>
			</Modal>
		</>
	);
};

export default DocumentUploadModal;
